import { AccessGate } from '../../shared/access/AccessControls';
import { useAllowedMutation, withAccess, useCan } from '../../shared/access/AccessControls';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
  Text,
  Divider,
  Checkbox,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { profilesApi } from '../../store/services/ProfilesService';
import { SubmitHandler, useForm } from 'react-hook-form';
import FormInput from '../../shared/components/FormInput';
import FormSelect from '../../shared/components/FormSelect';
import Input from '../../shared/components/ui/Input/Input';
import { pricingPlansApi } from '../../store/services/PricingPlansService';
import { useSelector } from 'react-redux';
import { selectPricingPlans } from '../../store/slices/pricingPlansSlice';
import { StyledInputsWrapper } from './styled';
import { SORTING_CREATED_AT } from '../pricingPlans/constants';
import { ASC } from '../../shared/constants';
import { CompanyRoles } from '../../store/responseTypes/profile';
import { companiesApi } from '../../store/services/CompaniesService';
import { useDebounce } from '../../shared/hooks/useDebounce';

type FormFields = {
  email: string;
  tariffPlanId: string;
  signupToCompany?: boolean;
  companyId?: string;
  companyRole?: CompanyRoles;
};

const companyRoles = [
  {
    label: 'Admin',
    value: CompanyRoles.admin,
  },
  {
    label: 'Employee',
    value: CompanyRoles.employee,
  },
];

const InviteUser = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [inviteUser, { isLoading, isSuccess, isError }] =
    useAllowedMutation(profilesApi.useInviteUserMutation, 'inviteUser');
  const toast = useToast();
  const [trigger] = pricingPlansApi.useLazyGetPlansQuery();
  const plans = useSelector(selectPricingPlans);

  const canFindCompanies = useCan('ADMIN_COMPANY_LIST');
  const canReadPlans = useCan('ADMIN_TARIFF_PLAN_LIST');
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const { data, isFetching, refetch } = companiesApi.useSearchCompaniesQuery({
    query: debouncedQuery,
  }, { skip: !isOpen || !canFindCompanies });

  useEffect(() => {
    if (isOpen && canFindCompanies) {
      refetch();
    }
  }, [isOpen, refetch, canFindCompanies]);

  const companiesOptions = useMemo(() => {
    return (data?.companies || []).map((company) => ({
      value: company.id,
      label: company.name,
    }));
  }, [data]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      email: '',
      tariffPlanId: '',
      signupToCompany: false,
      companyId: '',
    },
  });

  const companyId = watch('companyId');
  const signupToCompany = watch('signupToCompany');

  useEffect(() => {
    if (
      companiesOptions.length > 0 &&
      !watch('companyId') &&
      isOpen &&
      signupToCompany
    ) {
      setValue('companyId', companiesOptions[0].value);
    }
  }, [companiesOptions, setValue, watch, isOpen, signupToCompany]);

  useEffect(() => {
    if (!isOpen || !canReadPlans) return;
    trigger({
      limit: 20,
      offset: 0,
      sorting: SORTING_CREATED_AT,
      order: ASC,
      query: '',
    });
  }, [isOpen, canReadPlans, trigger]);

  const plansOptions = useMemo(() => {
    return plans
      .filter((plan) => !plan.isDeleted && plan.isActive)
      .map((plan) => ({ value: plan.id, label: plan.name }));
  }, [plans]);

  useEffect(() => {
    const defaultPlan = plans.find((plan) => plan.isDefault);
    if (defaultPlan && isOpen) {
      setValue('tariffPlanId', defaultPlan.id);
    }
  }, [plans, isOpen, setValue]);

  const sendInvite: SubmitHandler<FormFields> = async (formData) => {
    const payload = { ...formData };
    if (!payload.signupToCompany) {
      delete payload.companyId;
      delete payload.companyRole;
    }
    delete payload.signupToCompany;
    await inviteUser(payload);
  };

  const closeAndClear = () => {
    onClose();
    reset({
      email: '',
      tariffPlanId: '',
      signupToCompany: false,
      companyId: '',
      companyRole: CompanyRoles.admin,
    });
    setQuery('');
  };

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: 'Success.',
        description: 'Invitation has been sent.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      closeAndClear();
    }
    if (isError) {
      toast({
        title: 'Error.',
        description: 'Something went wrong. Check email validity',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }, [isSuccess, isError]);

  return (
    <Flex w={'100%'} justifyContent={'end'}>
      <Button
        onClick={onOpen}
        fontWeight={400}
        colorScheme={'blue'}
        w={'max-content'}
      >
        Invite user
      </Button>

      <Modal onClose={closeAndClear} size={'md'} isOpen={isOpen}>
        <ModalOverlay />
        <ModalContent mx={'1rem'}>
          <form onSubmit={handleSubmit(sendInvite)}>
            <ModalHeader>Invite user</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <StyledInputsWrapper>
                <FormInput
                  ph={'Email'}
                  {...{ register, name: 'email' }}
                  registerOptions={{ required: true }}
                  error={errors.email}
                />
                <FormSelect
                  ph={'Tariff plan'}
                  {...{ register, name: 'tariffPlanId' }}
                  labeledOptions={plansOptions}
                  registerOptions={{ required: true }}
                  error={errors.tariffPlanId}
                />

                <Checkbox {...register('signupToCompany')}>
                  Signup to company
                </Checkbox>

                {signupToCompany && (
                  <>
                    <Divider />

                    <Flex alignItems={'center'}>
                      <Input
                        name={'query'}
                        placeholder={'Search for company...'}
                        onChange={setQuery}
                      />
                    </Flex>

                    <FormSelect
                      ph={'Company'}
                      {...{ register, name: 'companyId' }}
                      labeledOptions={companiesOptions}
                      isDisabled={isFetching}
                      registerOptions={{ required: signupToCompany }}
                      error={errors.companyId}
                    />

                    <FormSelect
                      ph={'Company role'}
                      {...{ register, name: 'companyRole' }}
                      labeledOptions={companyRoles}
                      isDisabled={!companyId}
                    />
                  </>
                )}
              </StyledInputsWrapper>
            </ModalBody>
            <ModalFooter>
              <Flex w={'100%'} justifyContent={'center'}>
                <Button
                  type={'button'}
                  onClick={closeAndClear}
                  mr={2}
                  colorScheme={'red'}
                >
                  Close
                </Button>
                <AccessGate action="ADMIN_PROFILE_SIGNUP_INITIATE"><Button
                  type={'submit'}
                  colorScheme={'green'}
                  isLoading={isLoading}
                >
                  Send
                </Button></AccessGate>
              </Flex>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default withAccess(InviteUser, 'ADMIN_PROFILE_SIGNUP_INITIATE');
